import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Buildings, Check, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * B-009 · Org Switcher (modelo AEAT)
 * Dropdown que aparece en el topbar cuando el usuario tiene acceso a más de
 * una organización vía `also_org_ids`. Pensado sobre todo para gestores que
 * llevan varios talleres con un único perfil.
 *
 * Backend:
 *   GET  /api/me/orgs          → { items, active_org_id, primary_org_id }
 *   POST /api/auth/switch-org  → setea cookie `ag_active_org` (30 días)
 */
export default function OrgSwitcher() {
  const [orgs, setOrgs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [primaryId, setPrimaryId] = useState(null);
  const [switching, setSwitching] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/orgs");
      setOrgs(data.items || []);
      setActiveId(data.active_org_id || null);
      setPrimaryId(data.primary_org_id || null);
    } catch (err) {
      // No hide 5xx silently — log so DevOps spot regressions in `/api/me/orgs`.
      // Si el endpoint está caído (p.ej. backend reiniciando), el switcher
      // simplemente no se renderiza, pero queremos saberlo en consola.
      if (err?.response?.status >= 500) {
        // eslint-disable-next-line no-console
        console.warn("OrgSwitcher · /me/orgs failed", err?.response?.status, err?.response?.data);
      }
    }
  };
  useEffect(() => { load(); }, []);

  // Fase 2D · Mostrar contexto siempre. Si solo hay 1 org, muestra badge simple sin dropdown.
  if (!orgs || orgs.length === 0) return null;
  if (orgs.length === 1) {
    const only = orgs[0];
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-[var(--ag-border)] bg-[var(--ag-midnight-soft)] text-[var(--ag-text)] text-xs max-w-[220px]"
        data-testid="org-switcher-single"
      >
        {only?.logo_url ? (
          <img src={only.logo_url} alt="" className="w-5 h-5 rounded-sm object-cover" />
        ) : (
          <Buildings size={14} weight="duotone" className="text-[var(--ag-gold)] shrink-0" />
        )}
        <span className="truncate font-medium" data-testid="org-switcher-active-name">
          {only?.name || "—"}
        </span>
      </div>
    );
  }

  const active = orgs.find((o) => o.id === activeId) || orgs[0];

  const handleSwitch = async (org_id) => {
    if (org_id === activeId || switching) return;
    setSwitching(true);
    try {
      await api.post("/auth/switch-org", { org_id });
      // Recargamos para que TODO el shell (sidebar, dashboard, KPIs) refleje la nueva org.
      window.location.reload();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "No se pudo cambiar de organización");
      setSwitching(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-[var(--ag-border)] bg-[var(--ag-midnight-soft)] text-[var(--ag-text)] text-xs hover:border-[var(--ag-gold)]/40 transition-colors max-w-[220px]"
          data-testid="org-switcher-trigger"
          disabled={switching}
        >
          {active?.logo_url ? (
            <img src={active.logo_url} alt="" className="w-5 h-5 rounded-sm object-cover" />
          ) : (
            <Buildings size={14} weight="duotone" className="text-[var(--ag-gold)] shrink-0" />
          )}
          <span className="truncate font-medium" data-testid="org-switcher-active-name">
            {active?.name || "—"}
          </span>
          <CaretDown size={10} weight="bold" className="text-[var(--ag-text-dim)] shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72 bg-[var(--ag-midnight-soft)] border-[var(--ag-border)] text-[var(--ag-text)]"
        data-testid="org-switcher-menu"
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">
          Cambiar de centro · {orgs.length} disponibles
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[var(--ag-border)]" />
        {orgs.map((o) => (
          <DropdownMenuItem
            key={o.id}
            onClick={() => handleSwitch(o.id)}
            className="cursor-pointer text-[var(--ag-text)] focus:bg-[var(--ag-midnight)] focus:text-[var(--ag-text)]"
            data-testid={`org-switcher-item-${o.id}`}
          >
            <div className="flex items-center gap-2 w-full min-w-0">
              {o.logo_url ? (
                <img src={o.logo_url} alt="" className="w-7 h-7 rounded-sm object-cover shrink-0" />
              ) : (
                <div className="w-7 h-7 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] flex items-center justify-center shrink-0">
                  <Buildings size={14} weight="duotone" className="text-[var(--ag-gold)]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{o.name}</div>
                <div className="text-[10px] text-[var(--ag-text-dim)] flex items-center gap-1.5">
                  {o.city || "—"}
                  {o.is_primary && (
                    <span className="px-1 py-px rounded-sm bg-[var(--ag-gold)]/15 text-[var(--ag-gold)] text-[9px] uppercase tracking-wider font-bold">
                      Principal
                    </span>
                  )}
                </div>
              </div>
              {o.id === activeId && (
                <Check size={14} weight="bold" className="text-[var(--ag-gold)] shrink-0" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-[var(--ag-border)]" />
        <div className="px-2 py-2 text-[10px] text-[var(--ag-text-dim)] leading-snug">
          Cambiar de centro no afecta a tu sesión, sólo a la organización activa en esta pestaña.
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
